"use client";

import { forwardRef, useId } from "react";
import { cn } from "@/lib/utils";

interface TextareaProps extends React.ComponentProps<"textarea"> {
  label?: string;
  helperText?: string;
  error?: string;
  optional?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      helperText,
      error,
      optional,
      id: externalId,
      "aria-describedby": externalDescribedBy,
      ...props
    },
    ref
  ) => {
    const autoId = useId();
    const id = externalId || autoId;
    const helperId = `${id}-helper`;
    const errorId = `${id}-error`;

    const describedBy = [
      externalDescribedBy,
      error ? errorId : helperText ? helperId : undefined,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

    const textareaElement = (
      <textarea
        ref={ref}
        id={id}
        data-slot="textarea"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          [
            "flex min-h-[5rem] w-full",
            "rounded-[var(--radius-md)] border",
            "bg-transparent px-[var(--space-3)] py-[var(--space-2)]",
            "text-[length:var(--font-size-base)] md:text-[length:var(--font-size-sm)]",
            "text-[var(--color-text-primary)]",
            "placeholder:text-[var(--color-text-muted)]",
            "transition-colors duration-[var(--duration-normal)]",
            "outline-none resize-y",
            "border-[var(--color-border-default)]",
            "focus-visible:border-[var(--color-border-focus)]",
            "focus-visible:ring-[3px] focus-visible:ring-[var(--color-primary-muted)]",
            "aria-invalid:border-[var(--color-error)]",
            "aria-invalid:ring-[3px] aria-invalid:ring-[var(--color-error-muted)]",
            "disabled:pointer-events-none disabled:cursor-not-allowed",
            "disabled:bg-[var(--color-bg-muted)] disabled:opacity-50",
          ].join(" "),
          className
        )}
        {...props}
      />
    );

    if (!label && !helperText && !error) {
      return textareaElement;
    }

    return (
      <div className="flex flex-col gap-[var(--space-1-5)]">
        {label && (
          <label
            htmlFor={id}
            className="text-[length:var(--font-size-sm)] font-[number:var(--font-weight-medium)] text-[var(--color-text-primary)]"
          >
            {label}
            {optional && (
              <span className="ml-1 text-[length:var(--font-size-xs)] font-[number:var(--font-weight-normal)] text-[var(--color-text-muted)]">
                (optional)
              </span>
            )}
          </label>
        )}
        {textareaElement}
        {error ? (
          <p
            id={errorId}
            role="alert"
            className="text-[length:var(--font-size-xs)] text-[var(--color-error)]"
          >
            {error}
          </p>
        ) : helperText ? (
          <p
            id={helperId}
            className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)]"
          >
            {helperText}
          </p>
        ) : null}
      </div>
    );
  }
);
Textarea.displayName = "Textarea";

export { Textarea };
export type { TextareaProps };
